import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { Announcements, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PokerSiteId } from '@poker-stats/contracts';
import { globalSlice } from '../../containers/App/reducer';
import { selectUser } from '../../containers/App/selectors';
import {
  formatInactiveTabCloseOptionLabel,
  getInactiveTabCloseEnabledLocalStorage,
  getInactiveTabCloseLocalStorage,
  INACTIVE_TAB_CLOSE_CHANGED_EVENT,
  INACTIVE_TAB_CLOSE_OPTIONS,
  setInactiveTabCloseEnabledLocalStorage,
  setInactiveTabCloseLocalStorage,
} from '../../localStorage/inactiveTabCloseLocalStorage';
import type { InactiveTabCloseTimeoutSec } from '../../localStorage/inactiveTabCloseLocalStorage';
import V2Icon from '../components/V2Icon';
import V2Select from '../components/V2Select';
import './preferences.css';

const roomOptions: ReadonlyArray<{ label: string; value: PokerSiteId }> = [
  { label: 'PS', value: 1 },
  { label: 'CHI', value: 2 },
  { label: 'WPN', value: 3 },
  { label: '888', value: 4 },
  { label: 'CPR', value: 5 },
  { label: 'IP', value: 6 },
];

const defaultPriority = roomOptions.map(({ value }) => value);

function SortableRoom({ roomId, index }: { roomId: PokerSiteId; index: number }) {
  const { t } = useTranslation('translation');
  const roomLabel = roomOptions.find(({ value }) => value === roomId)?.label ?? String(roomId);
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: roomId });

  return (
    <li
      ref={setNodeRef}
      className={`v2-search-preferences__priority-room${isDragging ? ' is-dragging' : ''}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? 'none' : transition,
      }}
    >
      <span className="v2-search-preferences__priority-number" aria-hidden="true">
        {index + 1}
      </span>
      <span className="v2-search-preferences__room-label">{roomLabel}</span>
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="v2-search-preferences__drag-handle"
        {...attributes}
        {...listeners}
        aria-label={t('settings_move_room', { room: roomLabel })}
      >
        <svg viewBox="0 0 16 20" aria-hidden="true">
          <path d="M5 4h.01M11 4h.01M5 10h.01M11 10h.01M5 16h.01M11 16h.01" />
        </svg>
      </button>
    </li>
  );
}

function PreferenceSwitch({
  checked,
  onChange,
  label,
  id,
  describedBy,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  id: string;
  describedBy?: string;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      className="v2-search-preferences__switch"
      onClick={() => onChange(!checked)}
    >
      <span aria-hidden="true" />
    </button>
  );
}

export default function V2SearchPreferences() {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const smartSearchId = React.useId();
  const closeTabsId = React.useId();
  const closeTabsHintId = React.useId();
  const priorityHintId = React.useId();
  const [smartSearch, setSmartSearch] = React.useState(
    () => localStorage.getItem('smart-search') === 'true',
  );
  const [inactiveTabCloseEnabled, setInactiveTabCloseEnabled] = React.useState(
    getInactiveTabCloseEnabledLocalStorage,
  );
  const [inactiveTabCloseTimeout, setInactiveTabCloseTimeout] =
    React.useState<InactiveTabCloseTimeoutSec>(getInactiveTabCloseLocalStorage);
  const priority = React.useMemo(() => {
    const savedPriority = user?.autopasteRoomPriority ?? [];
    return [...new Set([...savedPriority, ...defaultPriority])].filter((roomId) =>
      defaultPriority.includes(roomId),
    );
  }, [user?.autopasteRoomPriority]);
  const roomsToSearch = user?.roomsToSearch ?? [];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const intervalOptions = React.useMemo(
    () =>
      INACTIVE_TAB_CLOSE_OPTIONS.map((value) => ({
        value,
        label: formatInactiveTabCloseOptionLabel(value, t),
      })),
    [t],
  );

  React.useEffect(() => {
    const syncPreferences = () => {
      setSmartSearch(localStorage.getItem('smart-search') === 'true');
      setInactiveTabCloseEnabled(getInactiveTabCloseEnabledLocalStorage());
      setInactiveTabCloseTimeout(getInactiveTabCloseLocalStorage());
    };
    window.addEventListener('storage', syncPreferences);
    window.addEventListener(INACTIVE_TAB_CLOSE_CHANGED_EVENT, syncPreferences);
    return () => {
      window.removeEventListener('storage', syncPreferences);
      window.removeEventListener(INACTIVE_TAB_CLOSE_CHANGED_EVENT, syncPreferences);
    };
  }, []);

  const changeRoom = (roomId: PokerSiteId, checked: boolean) => {
    const nextRooms = checked
      ? [...new Set([...roomsToSearch, roomId])]
      : roomsToSearch.filter((value) => value !== roomId);
    dispatch(globalSlice.actions.changeRoomsToSearch(nextRooms));
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = priority.indexOf(active.id as PokerSiteId);
    const newIndex = priority.indexOf(over.id as PokerSiteId);
    if (oldIndex < 0 || newIndex < 0) return;
    dispatch(
      globalSlice.actions.changeAutopasteRoomPriority(arrayMove(priority, oldIndex, newIndex)),
    );
  };

  const roomName = (roomId: string | number) =>
    roomOptions.find(({ value }) => value === roomId)?.label ?? String(roomId);
  const announcements: Announcements = {
    onDragStart: ({ active }) => t('settings_room_drag_start', { room: roomName(active.id) }),
    onDragOver: ({ active, over }) =>
      over
        ? t('settings_room_drag_over', {
            room: roomName(active.id),
            position: priority.indexOf(over.id as PokerSiteId) + 1,
            count: priority.length,
          })
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t('settings_room_drag_end', {
            room: roomName(active.id),
            position: priority.indexOf(over.id as PokerSiteId) + 1,
            count: priority.length,
          })
        : t('settings_room_drag_cancel'),
    onDragCancel: () => t('settings_room_drag_cancel'),
  };

  return (
    <>
      <section className="v2-settings-panel v2-search-preferences">
        <header className="v2-settings-panel__head">
          <span className="v2-settings-panel__icon">
            <V2Icon name="search" />
          </span>
          <div>
            <h2>{t('settings_search_autopaste')}</h2>
            <p className="v2-settings-panel__description">
              {t('settings_preferences_saved_automatically')}
            </p>
          </div>
        </header>

        <div className="v2-search-preferences__toggle-row">
          <label htmlFor={smartSearchId}>{t('smart_search')}</label>
          <PreferenceSwitch
            id={smartSearchId}
            checked={smartSearch}
            onChange={(checked) => {
              setSmartSearch(checked);
              localStorage.setItem('smart-search', String(checked));
            }}
            label={t('smart_search')}
          />
        </div>

        <fieldset className="v2-search-preferences__subsection">
          <legend>{t('rooms_for_search')}</legend>
          <p className="v2-settings-panel__description">{t('settings_rooms_hint')}</p>
          <div className="v2-search-preferences__rooms">
            {roomOptions.map(({ value, label }) => (
              <label key={value} className="v2-search-preferences__room-choice">
                <input
                  type="checkbox"
                  checked={roomsToSearch.includes(value)}
                  onChange={(event) => changeRoom(value, event.target.checked)}
                />
                <span className="v2-search-preferences__room-choice-surface">
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <path d="m3 8 3 3 7-7" />
                  </svg>
                  {label}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="v2-search-preferences__subsection">
          <h3>{t('autopaste_room_priority')}</h3>
          <p id={priorityHintId} className="v2-settings-panel__description">
            {t('settings_autopaste_priority_hint')}
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            accessibility={{
              announcements,
              screenReaderInstructions: { draggable: t('settings_dnd_instructions') },
            }}
          >
            <SortableContext items={priority} strategy={rectSortingStrategy}>
              <ol
                className="v2-search-preferences__priority"
                aria-label={t('autopaste_room_priority')}
                aria-describedby={priorityHintId}
              >
                {priority.map((roomId, index) => (
                  <SortableRoom key={roomId} roomId={roomId} index={index} />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        </div>
      </section>

      <section className="v2-settings-panel v2-search-preferences">
        <header className="v2-settings-panel__head">
          <span className="v2-settings-panel__icon">
            <V2Icon name="settings" />
          </span>
          <div>
            <h2>{t('inactive_tab_close')}</h2>
            <p id={closeTabsHintId} className="v2-settings-panel__description">
              {t('inactive_tab_close_hint')}
            </p>
          </div>
        </header>

        <div className="v2-search-preferences__toggle-row">
          <label htmlFor={closeTabsId}>{t('inactive_tab_close_enabled')}</label>
          <PreferenceSwitch
            id={closeTabsId}
            checked={inactiveTabCloseEnabled}
            onChange={(checked) => {
              setInactiveTabCloseEnabled(checked);
              setInactiveTabCloseEnabledLocalStorage(checked);
            }}
            label={t('inactive_tab_close_enabled')}
            describedBy={closeTabsHintId}
          />
        </div>

        <div className="v2-settings-field v2-search-preferences__timeout">
          <span>{t('settings_inactive_timeout')}</span>
          <V2Select
            ariaLabel={t('settings_inactive_timeout')}
            value={inactiveTabCloseTimeout}
            options={intervalOptions}
            disabled={!inactiveTabCloseEnabled}
            onChange={(value) => {
              setInactiveTabCloseTimeout(value);
              setInactiveTabCloseLocalStorage(value);
            }}
          />
        </div>
      </section>
    </>
  );
}
